import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import merge from 'lodash/merge'
import { useSnackbar } from 'notistack'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/legacy/utils/error-handling/throwifApiError'
import { handleInternalAPIResponse } from '@omnea-app/legacy/utils/general/observability/handleInternalAPIResponse'
import { ServiceUrls } from '@omnea-app/legacy/utils/general/serviceUrls'
import {
  RequestPreferences,
  UpdateUserPreferencesRequestSchema,
  NotificationPreferencesSchema,
  UserPreferencesResponseSchema,
} from '@omnea-app/users/usersFrontendClient'
import { UsersHttpClient } from '@omnea-app/users/usersHttpClient'

export type Preferences = z.infer<typeof UserPreferencesResponseSchema>
export type PartialPreferences = z.infer<typeof UpdateUserPreferencesRequestSchema>

export interface UserPreferencesContext {
  data: Preferences
  updatePreferences: (data: PartialPreferences) => void
}

export const useUserPreferences = (): UserPreferencesContext => {
  const { user, getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('settings')

  const { data } = useSuspenseQuery<Preferences, Error>({
    queryKey: ['userPreferences', user.id],
    queryFn: async () => {
      const token = await getToken()
      const { data, error } = await new UsersHttpClient({ omneaJwt: token }).GET('/{userId}/preferences', {
        params: { path: { userId: user.id } },
      })
      throwIfApiError(error)
      return data
    },
  })

  const { mutateAsync } = useMutation<Preferences, Error, PartialPreferences>({
    mutationFn: async (updatedPreferences) => {
      const previousData = queryClient.getQueryData(['userPreferences', user.id])

      // Optimistic update
      queryClient.setQueryData(['userPreferences', user.id], (currentSettings: Preferences) =>
        merge({}, currentSettings, updatedPreferences)
      )

      const token = await getToken()
      const { data, error } = await new UsersHttpClient({ omneaJwt: token }).POST('/{userId}/preferences', {
        body: {
          localizationPreferences: updatedPreferences.localizationPreferences,
          notificationPreferences: updatedPreferences.notificationPreferences,
          dateFormatPreferences: updatedPreferences.dateFormatPreferences,
        },
        params: { path: { userId: user.id } },
      })

      if (error) {
        queryClient.setQueryData(['userPreferences', user.id], previousData)
      }

      throwIfApiError(error)

      return data
    },
    onError: () => {
      enqueueSnackbar(t('user-settings.update-error'), { variant: 'error' })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['userPreferences', user.id] })
    },
  })

  return {
    data,
    updatePreferences: mutateAsync,
  }
}

export const useOrganisationNotificationPreferences = () => {
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()

  type Preferences = z.infer<typeof NotificationPreferencesSchema>
  const {
    data,
    isLoading: isQueryLoading,
    isError: isQueryError,
    error: queryError,
  } = useQuery<Preferences, Error>({
    queryKey: ['organisationNotificationPreferences'],
    queryFn: async () => {
      const token = await getToken()
      const { data, error } = await new UsersHttpClient({
        omneaJwt: token,
      }).GET('/organisation/preferences')
      throwIfApiError(error)
      return data
    },
  })
  const {
    mutate,
    isPending: isMutateLoading,
    isError: isMutateError,
    error: mutateError,
  } = useMutation<void, Error, Preferences>({
    mutationFn: async (updatedPreferences) => {
      const token = await getToken()
      const { error } = await new UsersHttpClient({
        omneaJwt: token,
      }).POST('/organisation/preferences', {
        body: updatedPreferences,
      })
      throwIfApiError(error)
      return
    },
    onSuccess: async (_) => {
      await queryClient.invalidateQueries({
        queryKey: ['organisationNotificationPreferences'],
      })
    },
  })

  const isError = (_error: Error | null): _error is Error => {
    return isQueryError || isMutateError
  }

  return {
    data,
    isLoading: isQueryLoading || isMutateLoading,
    isError,
    error: queryError || mutateError,
    updatePreferences: mutate,
  }
}

export const useUserRequestPreferences = (requestId: string) => {
  const { user, getToken } = useAuthenticatedUser()
  const { enqueueSnackbar } = useSnackbar()

  type Preferences = z.infer<typeof UserPreferencesResponseSchema>
  const {
    data,
    isLoading: isQueryLoading,
    isError: isQueryError,
    error: queryError,
    refetch,
  } = useQuery<Preferences, Error>({
    queryKey: ['userRequestPreferences', user.id, requestId],
    queryFn: async () => {
      const token = await getToken()
      return fetch(`${ServiceUrls.USERS}/${user.id}/preferences?requestId=${requestId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then(handleInternalAPIResponse)
    },
  })

  const {
    mutate,
    isPending: isMutateLoading,
    isError: isMutateError,
    error: mutateError,
  } = useMutation<RequestPreferences, Error, RequestPreferences>({
    mutationFn: async (requestPreferences) => {
      const token = await getToken()
      const { data, error } = await new UsersHttpClient({
        omneaJwt: token,
      }).POST('/{userId}/request-preferences', {
        body: { requestPreferences },
        params: { path: { userId: user.id } },
      })
      throwIfApiError(error)
      return data.requestPreferences
    },
    onSuccess: () => {
      void refetch()
    },
  })

  const error = queryError || mutateError
  const isError = (_error: Error | null): _error is Error => {
    return isQueryError || isMutateError
  }

  useEffect(() => {
    if (isError(error)) {
      enqueueSnackbar(error?.message, { variant: 'error' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, error])

  const hasNotificationsEnabled =
    data?.notificationPreferences?.channels?.email ||
    data?.notificationPreferences?.channels?.slack ||
    data?.notificationPreferences?.channels?.teams

  return {
    isMuted: data?.requestPreferences?.isMuted ?? false,
    isLoading: isQueryLoading || isMutateLoading,
    hasNotificationsEnabled: hasNotificationsEnabled ?? true,
    shouldUnmute: (isUnmuted: boolean) => mutate({ id: requestId, isMuted: !isUnmuted }),
  }
}
